<template>
	<div ref="el" class="infinite-bi-scroll">
		<template v-if="enabled">
			<Spinner v-if="loadingPrev" class="spinner top" name="dots" />
			<div :class="{ 'scrollable-area': true, 'scrollable-area--top': pageStart > 1 }"></div>
		</template>

		<slot v-bind="{ anchorName: ANCHOR_NAME, page, pageStart, pageNumber }" />

		<div v-if="enabled" class="pagination-container">
			<Spinner v-if="loadingNext && page <= lastPage" class="spinner bottom" name="dots" />

			<PaginationNav
				v-if="!anyLoading && page <= lastPage"
				:page-size="pageSize"
				:total-count="
					titlesAmount > lastPage * pageSize ? lastPage * pageSize : titlesAmount || lastPage * pageSize
				"
				:last-page="lastPage"
				:show-first-jump="true"
				:show-last-jump="showLastJump"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import { useBiScroll } from '@/helpers/composables/useBiScroll';
import { computed, ref, toRefs } from 'vue';

import type { LoadMorePayload } from '@/helpers/composables/useBiScroll';

import PaginationNav from '@/components/nav/PaginationNav.vue';
import Spinner from '@/ui-components/Spinner.vue';
import { PaginationLevel } from '@/stores/pinia/useMetaStore';

interface InfiniteBiScrollProps {
	paginationLevel: PaginationLevel;
	loading: boolean;
	pageSize: number;
	titlesAmount?: number;
	showLastJump?: boolean;
}

const props = withDefaults(defineProps<InfiniteBiScrollProps>(), {
	titlesAmount: 0,
});

const enabled = computed(() => props.paginationLevel > 0);

// emits an event with payload to stay compatible with <CustomInfiniteScroll>
// that uses event.target?.complete() after loadMore() events.
const emit = defineEmits<{
	(e: 'load-next' | 'load-prev', payload: LoadMorePayload): void;
}>();

const el = ref<HTMLElement | null>(null);

const onEmit = (eventName: 'load-next' | 'load-prev', payload: LoadMorePayload) => {
	emit(eventName, payload);
};

function pageNumber(pageStart: number, index: number, titlesPerPage: number) {
	return Math.floor(pageStart - 1 + (index / titlesPerPage + 1));
}

const { page, pageStart, lastPage, loadingPrev, loadingNext, anyLoading, ANCHOR_NAME } = useBiScroll(
	el,
	toRefs(props),
	onEmit
);
</script>

<style lang="scss" scoped>
.infinite-bi-scroll {
	position: relative;
}

.spinner {
	width: 100%;
	position: absolute;
	left: 0;

	&.top {
		transform: translateY(-10px);
	}

	&.bottom {
		transform: translateY(-35px);
	}
}

.scrollable-area {
	width: 100%;

	&--top {
		height: 20px;
	}
}

$margin-width: 15px;

.pagination-container {
	position: absolute; // prevent CLS
	margin: $margin-width;
	margin-top: 3 * $margin-width;
	width: calc(100% - 2 * #{$margin-width});
	// derived from max-width set in .container-fluid.container-max-width
	// usually this wouldn't be necessary, but since the pagination is position: aboslute, we need a max-width, too.
	max-width: 1624px;

	@media (min-width: 1050px) {
		width: calc(100% - 130px);
		margin-left: 0;
		margin-right: 0;
	}
}
</style>
